/**
 * Partner Attribution Tracking - Checkout Frontend
 *
 * Manages partner attribution cookies that can be set from URL parameters.
 * Cookie is shared across refurbly.se and checkout.refurbly.se
 */

export interface PartnerAttribution {
	partner: string;
	timestamp: number;
	source: string;
	campaign?: string;
	medium?: string;
}

// Whitelist of valid partners to prevent fake attributions
const VALID_PARTNERS = [
	'chilimobil',
	// Add more partners here as needed
] as const;

type ValidPartner = typeof VALID_PARTNERS[number];

const COOKIE_NAME = 'partner_ref';
const COOKIE_DOMAIN = '.refurbly.se'; // Shared across subdomains
const DEFAULT_EXPIRY_DAYS = 7;

/**
 * Validates if a partner identifier is in the whitelist
 */
function isValidPartner(partner: string): partner is ValidPartner {
	return VALID_PARTNERS.includes(partner.toLowerCase() as ValidPartner);
}

/**
 * Sets partner attribution from URL parameters
 *
 * @param partner - Partner identifier (e.g., 'chilimobil')
 * @param options - Additional attribution data
 * @param expiryDays - Days until cookie expires (default: 7)
 * @returns true if attribution was set successfully
 */
export function setPartnerAttribution(
	partner: string,
	options: {
		source?: string;
		campaign?: string;
		medium?: string;
	} = {},
	expiryDays: number = DEFAULT_EXPIRY_DAYS
): boolean {
	// Validate partner
	if (!isValidPartner(partner)) {
		console.warn(`[Partner Attribution] Invalid partner: ${partner}`);
		return false;
	}

	const attribution: PartnerAttribution = {
		partner: partner.toLowerCase(),
		timestamp: Date.now(),
		source: options.source || (typeof window !== 'undefined' ? window.location.pathname : ''),
		...(options.campaign && { campaign: options.campaign }),
		...(options.medium && { medium: options.medium })
	};

	// Calculate expiry date
	const expiryDate = new Date();
	expiryDate.setDate(expiryDate.getDate() + expiryDays);

	// Set cookie (accessible on both refurbly.se and checkout.refurbly.se)
	const cookieValue = encodeURIComponent(JSON.stringify(attribution));
	const cookieString = `${COOKIE_NAME}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; domain=${COOKIE_DOMAIN}; SameSite=Lax; Secure`;

	if (typeof document !== 'undefined') {
		document.cookie = cookieString;
		console.log('[Partner Attribution] Set:', attribution);
		return true;
	}

	return false;
}

/**
 * Gets partner attribution from cookie
 *
 * @returns PartnerAttribution object or null if not found/expired
 */
export function getPartnerAttribution(): PartnerAttribution | null {
	if (typeof document === 'undefined') return null;

	const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
	if (!match) return null;

	try {
		const attribution = JSON.parse(decodeURIComponent(match[1])) as PartnerAttribution;

		// Validate attribution hasn't expired
		if (!isAttributionValid(attribution)) {
			console.log('[Partner Attribution] Expired attribution found, ignoring');
			return null;
		}

		return attribution;
	} catch (error) {
		console.error('[Partner Attribution] Failed to parse cookie:', error);
		return null;
	}
}

/**
 * Checks if attribution is still valid based on age
 *
 * @param attribution - Attribution object to validate
 * @param maxAgeDays - Maximum age in days (default: 7)
 * @returns true if attribution is still valid
 */
export function isAttributionValid(
	attribution: PartnerAttribution,
	maxAgeDays: number = DEFAULT_EXPIRY_DAYS
): boolean {
	const ageInDays = (Date.now() - attribution.timestamp) / (1000 * 60 * 60 * 24);
	return ageInDays <= maxAgeDays;
}

/**
 * Gets attribution age in minutes (useful for analytics)
 */
export function getAttributionAge(attribution: PartnerAttribution): number {
	return (Date.now() - attribution.timestamp) / (1000 * 60);
}

/**
 * Gets attribution age in a human-readable format
 */
export function getAttributionAgeFormatted(attribution: PartnerAttribution): string {
	const ageMinutes = getAttributionAge(attribution);

	if (ageMinutes < 60) {
		return `${Math.floor(ageMinutes)} minutes`;
	}

	const ageHours = ageMinutes / 60;
	if (ageHours < 24) {
		return `${Math.floor(ageHours)} hours`;
	}

	const ageDays = ageHours / 24;
	return `${Math.floor(ageDays)} days`;
}

/**
 * Tracks attribution in PostHog (if available)
 */
export function trackAttributionInPostHog(attribution: PartnerAttribution): void {
	if (typeof window === 'undefined' || !(window as any).posthog) return;

	const posthog = (window as any).posthog;

	posthog.capture('partner_checkout_started', {
		partner: attribution.partner,
		source: attribution.source,
		campaign: attribution.campaign,
		medium: attribution.medium,
		age_minutes: Math.floor(getAttributionAge(attribution)),
		age_formatted: getAttributionAgeFormatted(attribution)
	});

	// Set user property for segmentation
	posthog.people.set({
		last_partner_ref: attribution.partner,
		last_partner_ref_time: new Date(attribution.timestamp).toISOString()
	});
}

/**
 * Exports attribution as metadata format for order API
 */
export function getAttributionMetadata(): Record<string, string> | null {
	const attribution = getPartnerAttribution();
	if (!attribution) return null;

	return {
		partner_ref: attribution.partner,
		partner_timestamp: attribution.timestamp.toString(),
		partner_source: attribution.source,
		partner_age_minutes: Math.floor(getAttributionAge(attribution)).toString(),
		...(attribution.campaign && { partner_campaign: attribution.campaign }),
		...(attribution.medium && { partner_medium: attribution.medium })
	};
}
